/*
    This script contains the app to be rendered on the DOM when developing for
    the foundation of the frontend
*/
import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Switch, Route, Redirect } from "react-router-dom"
import { useLDClient, useFlags } from "launchdarkly-react-client-sdk"
import { useDispatch } from "react-redux"
import { SplashScreenHandler } from "ksc-portal-common-components/atoms"
import { KR_THEME } from "ksc-portal-shareable-configs/dist/webpack/webpack-foundation/constants"
import { SonnerToaster, Toaster, makeGetRequest, sonner } from "common/utils"
import { SidebarProvider } from "ksc-portal-common-components/shadcn"
import theme from "../../client/src/utils/theme"
import NotVerified from "./routers/notVerified"
import Logout from "../../client/src/CoreModules/Logout"
import Navbar from "../../client/src/CoreModules/Navbar"
import Modal from "../../client/src/CoreModules/Modal"
import OldToaster from "../../client/src/CoreModules/OldToast"
import Legal from "../../client/src/CoreModules/Legal"
import SSOCallback from "../../client/src/CoreModules/SSO/Callback"
import SSOLogin from "../../client/src/CoreModules/SSO/Login"
import AnalyticsManagment from "../../client/src/CoreModules/AnalyticsManagement"
import SessionManagement from "../../client/src/CoreModules/SessionManagement"
import SystemDown from "../../client/src/CoreModules/SystemDown"
import WindowFocusHandler from "../Utilities/WindowFocusHandler"
import AsyncVerifiedComponent from "../Utilities/Async/VerifiedComponent"
import Package from "./package"
import { LEGAL_CONST } from "../../client/src/CoreModules/Legal/constants"
import { setUserDetails } from "../../client/src/CoreModules/SessionManagement/actionCreators"
import { forceProfileRedirect } from "../../lib"
import ErrorBoundary from "../../client/src/CoreModules/ErrorBoundary/component"

const App = ({
  loggedIn,
  register,
  tcManager,
  render,
  store,
  history,
  cookies,
  isSidebarOpen,
}) => {
  const ldClient = useLDClient()
  const dispatch = useDispatch()
  const { srbSystemDownMaintenance, hubSystemDownMaintenance } = useFlags()
  const renderSplashScreen = [KR_THEME.theme].includes(theme)
  const [splashScreenFinished, setSplashscreenFinished] = useState(
    !renderSplashScreen
  )
  const { pathname } = history.location
  const isLoginPath = pathname.includes("/login")
  const state = store.getState()
  const {
    userDetails,
    idTokenPayload: { email, "custom:entity_id": entity_id },
  } = state.sessionReducer

  useEffect(() => {
    if (!loggedIn) return
    /**
     * Get user details
     *
     */
    makeGetRequest({
      url: "/iam_v1/me/profile",
      onSuccess: (resp) => dispatch(setUserDetails(resp)),
      onFailure: (err) => {
        sonner.error("Could not get profile details")
        console.error(err)
      },
    })
    /**
     * Feature Flag User Identification
     *
     */
    // skip ID Cypress E2E user
    if (email.startsWith("cypress-")) return

    const isSRB = document.title === "Single Rulebook"
    const clientName = isSRB
      ? "singlerulebook"
      : window.location.hostname.split(".")[0]
    const context = {
      kind: "account",
      key: `account-${clientName}-${entity_id}`,
      name: clientName,
      entity_id,
    }
    ldClient.identify(context, null, () => {
      console.log("[LaunchDarkly] Identify ", context)
    })
  }, [loggedIn])

  /**
   * Redirect first time or 1 year lapsed since last updated profile details
   *
   */
  useEffect(() => {
    const EXCLUDED_PATHS = ["/profile/details", "/legal", "/logout"]
    if (EXCLUDED_PATHS.some((path) => pathname.includes(path))) return
    if (userDetails) {
      const { last_updated } = userDetails
      if (forceProfileRedirect(last_updated)) {
        history.push("/profile/details")
        sonner.warning("Profile information required before proceeding")
      }
    }
  }, [pathname, userDetails])

  /**
   * Maintenance Flag
   *
   */
  if (
    (document.title === "Single Rulebook" && srbSystemDownMaintenance) ||
    (document.title === "Kaizen Hub" && hubSystemDownMaintenance)
  )
    return <SystemDown history={history} type="maintenance" />

  return (
    <SidebarProvider
      style={{
        "--sidebar-width": "378px",
      }}
      className="min-h-[unset]"
      defaultOpen={false}
      open={isSidebarOpen}
    >
      <ErrorBoundary>
        <Switch>
          <Route path="/logout">
            <Logout
              setSplashscreenFinished={
                renderSplashScreen ? setSplashscreenFinished : null
              }
            />
          </Route>
          <Route path="/sso/callback">
            <SSOCallback history={history} cookies={cookies} />
          </Route>
          <Route path="/error/500">
            <SystemDown history={history} />
          </Route>
          <Route path="*">
            <WindowFocusHandler cookies={cookies} />
            <AnalyticsManagment />
            <SessionManagement />
            <Switch>
              <Route path="*">
                {render && (
                  <Switch>
                    <Route path="/sso/login">
                      <SSOLogin history={history} />
                    </Route>
                    <Route path="*">
                      {loggedIn !== null && (
                        <>
                          <Navbar loggedIn={loggedIn} />
                          <main
                            className={`app-div-style${
                              isLoginPath ? " app-div-style--is-kaizen" : ""
                            }`}
                          >
                            <Switch>
                              <Route path={`/${LEGAL_CONST}`}>
                                <Legal loggedIn={loggedIn} history={history} />
                              </Route>

                              <Route path="*">
                                {!loggedIn && <NotVerified />}
                                {loggedIn &&
                                  tcManager &&
                                  (tcManager.userHasAcceptedAllThemeTCs() ? (
                                    <>
                                      <AsyncVerifiedComponent
                                        store={store}
                                        register={register}
                                        splashScreenFinished={
                                          splashScreenFinished
                                        }
                                        moduleProvider={register(
                                          "foundation",
                                          import(
                                            /* webpackChunkName:"foundation" */
                                            `./routers/verified`
                                          ),
                                          false,
                                          false
                                        )}
                                      />
                                      {renderSplashScreen &&
                                        !splashScreenFinished && (
                                          <div className="h-full">
                                            <SplashScreenHandler
                                              theme={theme}
                                              setSplashscreenFinished={
                                                setSplashscreenFinished
                                              }
                                            />
                                          </div>
                                        )}
                                    </>
                                  ) : (
                                    <Redirect
                                      to={tcManager.nextOutstandingUrl()}
                                    />
                                  ))}
                              </Route>
                            </Switch>
                          </main>
                        </>
                      )}
                      <Modal />
                      <OldToaster />
                      <Toaster />
                      <SonnerToaster />
                    </Route>
                  </Switch>
                )}
              </Route>
            </Switch>
          </Route>
        </Switch>
      </ErrorBoundary>
    </SidebarProvider>
  )
}

App.propTypes = {
  isSidebarOpen: PropTypes.bool.isRequired,
  register: PropTypes.func,
  loggedIn: PropTypes.bool,
  tc: PropTypes.shape({}),
  render: PropTypes.bool.isRequired,
  store: PropTypes.shape({
    getState: PropTypes.func,
  }).isRequired,
  history: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
    push: PropTypes.func,
  }).isRequired,
  cookies: PropTypes.shape({}).isRequired,
  tcManager: PropTypes.shape({
    userHasAcceptedAllThemeTCs: PropTypes.func.isRequired,
    nextOutstandingUrl: PropTypes.func.isRequired,
  }),
}

App.defaultProps = {
  register: () => null,
  loggedIn: false,
  tc: null,
  tcManager: null,
}

Package(App)

export default App
